import { batch, computed, signal } from "@preact/signals-react"

// Table data
export const tableData = signal([])
export const totalCount = signal(0)
export const isLoadingTable = signal(false)
export const latestId = signal(0)

// Streaming control
export const autoRefresh = signal(true)
export const refreshInterval = signal(30000) // 30 seconds
export const lastRefreshTime = signal(null)
export const nextRefreshCountdown = signal(30)

// Filters
export const filterProductId = signal('')
export const filterIdSites = signal([])
export const filterIdSku = signal(null) // Object with value property from autocomplete
export const filterUserId = signal(null)
export const filterAction = signal([]) // ['MAP', 'UNMAP', 'REMAP']
export const filterMappingType = signal([]) // ['raw2sku', 'sku2raw', 'raw2raw']
export const filterSource = signal([])
export const filterDateFrom = signal(null)
export const filterDateTo = signal(null)
export const filterQuick = signal('today') // 'none', 'lastHour', 'today', 'thisWeek'
export const excludePropagated = signal(false)

// Pagination
export const currentPage = signal(0)
export const pageSize = signal(100)

// Edit Mapping Drawer
export const editMappingDrawer = signal(null)
export const imageComparisonModal = signal(null)

// User Analysis Drawer
export const userAnalysisOpen = signal(false)
export const selectedUserForAnalysis = signal(null)
export const userAnalysisData = signal(null)
export const userActivityData = signal(null)
export const isLoadingUserAnalysis = signal(false)

// Filter options from backend
export const usersOptions = signal([])
export const sourcesOptions = signal([])
export const sitesOptions = signal([])

// Computed: filters as object for API call
export const filtersValue = computed(() => ({
    product_id: filterProductId.value || undefined,
    id_sites: filterIdSites.value.length > 0 ? filterIdSites.value.map(s => s.value) : undefined,
    id_sku: filterIdSku.value || undefined,
    user_id: filterUserId.value || undefined,
    action: filterAction.value.length > 0 ? filterAction.value : undefined,
    mapping_type: filterMappingType.value.length > 0 ? filterMappingType.value : undefined,
    source: filterSource.value.length > 0 ? filterSource.value.map(s => s.value) : undefined,
    date_from: filterDateFrom.value || undefined,
    date_to: filterDateTo.value || undefined,
    exclude_propagated: excludePropagated.value || undefined,
    page: currentPage.value,
    pageSize: pageSize.value
}))

// Clear all signals
export const clearAuditLogSignals = () => batch(() => {
    tableData.value = []
    totalCount.value = 0
    isLoadingTable.value = false
    latestId.value = 0
    autoRefresh.value = true
    lastRefreshTime.value = null
    nextRefreshCountdown.value = 30
    filterProductId.value = ''
    filterIdSites.value = []
    filterIdSku.value = null
    filterUserId.value = null
    filterAction.value = []
    filterMappingType.value = []
    filterSource.value = []
    filterDateFrom.value = null
    filterDateTo.value = null
    filterQuick.value = 'today'
    excludePropagated.value = false
    currentPage.value = 0
    editMappingDrawer.value = null
    imageComparisonModal.value = null
    userAnalysisOpen.value = false
    selectedUserForAnalysis.value = null
    userAnalysisData.value = null
    userActivityData.value = null
})

// Clear only filters (keep data)
export const clearFilters = () => batch(() => {
    filterProductId.value = ''
    filterIdSites.value = []
    filterIdSku.value = null
    filterUserId.value = null
    filterAction.value = []
    filterMappingType.value = []
    filterSource.value = []
    filterDateFrom.value = null
    filterDateTo.value = null
    filterQuick.value = 'none'
    excludePropagated.value = false
    currentPage.value = 0
})

// Apply quick filter dates
export const applyQuickFilter = (quickFilter) => {
    const now = new Date()
    const today = now.toISOString().split('T')[0]

    batch(() => {
        filterQuick.value = quickFilter

        switch (quickFilter) {
            case 'lastHour': {
                // Send full ISO datetime for precise hour filtering
                const oneHourAgo = new Date(now - 60 * 60 * 1000)
                filterDateFrom.value = oneHourAgo.toISOString()
                filterDateTo.value = now.toISOString()
                break
            }
            case 'today': {
                filterDateFrom.value = today
                filterDateTo.value = today
                break
            }
            case 'thisWeek': {
                const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000)
                filterDateFrom.value = weekAgo.toISOString().split('T')[0]
                filterDateTo.value = today
                break
            }
            case 'allTime':
                // No date filtering - search all history
                filterDateFrom.value = null
                filterDateTo.value = null
                break
            case 'none':
            default:
                // Custom - keep current values or null
                break
        }
    })
}
